import React from 'react';
import { useFormikContext, useField, FormikValues, FormikProps } from 'formik';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { OptionValue } from '../../../services/types/Misc';
import { safeFind } from '../../misc/utils';

interface Props {
  label?: string | React.ReactNode;
  name: string;
  elements: OptionValue[];
  size?: string;
  className?: string;
  icon?: IconProp;
  value: string;
  onChangeAction?: (selectedValue?: string) => void;
  noMargin?: boolean;
  disabled?: boolean;
  clickable?: boolean;
  placeholder?: string;
  menuWidth?: string;
  showCheck?: boolean;
}

const FormDropdown = (props: Props) => {
  const {
    label,
    name,
    elements,
    value,
    className,
    size,
    icon,
    onChangeAction,
    noMargin,
    disabled,
    clickable = true,
    placeholder,
    menuWidth,
    showCheck = true,
  } = props;
  const { t } = useTranslation();
  const form = useFormikContext<FormikValues>();
  const [field, meta] = useField(name);

  const onChange = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    selectedValue: string | undefined
  ) => {
    e.preventDefault();
    form.setFieldValue(name, selectedValue || null, false);
    if (onChangeAction) {
      onChangeAction(selectedValue);
    }
    setTimeout(() => {
      form.setFieldTouched(name, true);
    }, 0);
  };

  const drawItem = (opt: OptionValue) => {
    const active = opt.value === field.value;
    return (
      <DropdownItem
        disabled={!clickable}
        key={opt.id}
        onClick={(e) => onChange(e, opt.value)}
        active={false}
      >
        {opt.text} {active && showCheck && <FontAwesomeIcon icon="check" />}
      </DropdownItem>
    );
  };

  const getValidValue = () => {
    if (value) {
      const safeValue = safeFind(elements, (x) => x.value === value).text;
      if (safeValue) {
        return safeValue;
      }
    }
    return placeholder || t('chooseSelectOption');
  };

  const classes = `${className || ''} ${icon ? 'icon' : ''}`;
  const hasError = meta.touched && meta.error;
  return (
    <div className={`form__form-group ${noMargin ? 'mb-0' : ''}`}>
      {label && <span className="form__form-group-label">{label}</span>}
      <div className="form__form-group-field">
        <div className="form__form-group-input-wrap">
          <UncontrolledDropdown size={size} className={classes}>
            <DropdownToggle
              id={name}
              className={`${hasError && 'error'} ${icon ? 'icon' : ''}`}
              outline
              color="nothing"
              disabled={disabled}
            >
              <p>
                {icon && <FontAwesomeIcon icon={icon} />}
                {getValidValue()}
                <FontAwesomeIcon icon="chevron-down" />
              </p>
            </DropdownToggle>
            <DropdownMenu
              className="dropdown__menu"
              style={menuWidth ? { minWidth: menuWidth } : undefined}
            >
              {elements.map((e) => drawItem(e))}
            </DropdownMenu>
          </UncontrolledDropdown>
          {hasError && <span className="form__form-group-error">{meta.error}</span>}
        </div>
      </div>
    </div>
  );
};

export default FormDropdown;

export const castToSelectValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

export const handleNumericChange =
  <Values extends FormikValues>(
    formik: FormikProps<Values>,
    fieldName: string,
    options: OptionValue[]
  ) =>
  (selectedValue?: string) => {
    // Buscamos el objeto para recuperar el ID original
    const found = options.find((opt) => opt.value === selectedValue);

    // Guardamos el ID (number) en Formik
    formik.setFieldValue(fieldName, found ? found.id : null);
  };

export const handleBooleanChange =
  <Values extends FormikValues>(formik: FormikProps<Values>, fieldName: string) =>
  (selectedValue?: string) => {
    // Cambiamos el parámetro a string
    let valueToSave: boolean | null;

    if (!selectedValue || selectedValue === '') {
      valueToSave = null;
    } else {
      // Convertimos el string '1' a true, lo demás a false
      valueToSave = selectedValue === '1';
    }

    formik.setFieldValue(fieldName, valueToSave);
  };
